import { getGPUTier } from "detect-gpu";

const SOFTWARE_RENDERER_MARKERS = [
    "swiftshader",
    "warp",
    "microsoft basic render driver",
    "llvmpipe",
    "software",
] as const;

const INTEGRATED_RENDERER_MARKERS = [
    /\bintel\b/i,
    /\bradeon(?:\(tm\))?\sgraphics\b/i,
    /\bradeon\s+\d+m\b/i,
    /\bvega\b/i,
] as const;

// Discrete Intel Arc dGPUs (Alchemist A-series, Battlemage B-series), including mobile A770M.
// Core Ultra iGPUs branded "Arc Graphics" / "Arc 140V" / "Arc 130T" must not match this.
const DISCRETE_INTEL_ARC_MARKER = /\barc\s+[ab]\d/i;

// Names that positively identify a discrete GPU. Used as counter-evidence so a *weak*
// discrete GPU (low benchmark tier) is not misclassified as an integrated one.
const DISCRETE_RENDERER_MARKERS = [
    /\bnvidia\b/i,
    /\bgeforce\b/i,
    /\bquadro\b/i,
    /\b[rg]tx\b/i,
    /\bradeon\s+(rx|pro|r9|r7)\b/i,
    DISCRETE_INTEL_ARC_MARKER,
    /\bapple\b/i,
    /\bmetal\b/i,
] as const;

type NavigatorUserAgentDataLike = {
    platform?: string;
};

type GPUAdapterInfoLike = {
    vendor?: string;
    architecture?: string;
    device?: string;
    description?: string;
};

type GPUAdapterLike = {
    info?: GPUAdapterInfoLike;
    isFallbackAdapter?: boolean;
    requestAdapterInfo?: () => Promise<GPUAdapterInfoLike>;
};

type NavigatorGpuLike = {
    requestAdapter?: () => Promise<GPUAdapterLike | null>;
};

export type WebGlProbeResult = {
    status: "hardware" | "software" | "unavailable";
    renderer?: string;
};

export type WebGpuProbeResult = {
    status: "available" | "unavailable" | "unsupported";
    adapterInfo?: GPUAdapterInfoLike;
    isFallbackAdapter?: boolean;
};

export type Layer1Classification = {
    status: "acceleration-off" | "degraded" | "ok";
    evidence: string[];
    hardwareAccelerated: boolean;
    corroboratedByWebGpu: boolean;
};

export type Layer2Classification = {
    /**
     * Identity-based status: what GPU the browser appears to be running on. Decided by
     * name/vendor evidence only — performance numbers never flip this (a weak discrete
     * GPU benchmarks like an iGPU but is not a misconfiguration).
     */
    status: "likely-igpu" | "likely-discrete" | "unknown";
    evidence: string[];
    confidence: "low" | "medium" | "high";
    /**
     * Secondary performance signal: the active GPU (whichever it is) benchmarks low.
     * With status "likely-discrete" this means the hardware itself is the likely limit;
     * with "likely-igpu" it corroborates the misconfiguration.
     */
    weakGpu: boolean;
    weakGpuEvidence: string[];
};

export type DetectGpuRawResult = {
    status: "ok" | "failed";
    /** Benchmark tier before novoweb's heuristics (RTX/Quadro bumps, Intel HD demotion) are applied. */
    tier?: number;
    fps?: number;
    gpu?: string;
    isMobile?: boolean;
};

/**
 * Re-runs detect-gpu with the same thresholds as `loadDeviceTier` (useHandleInit.ts)
 * but without the tier-adjustment heuristics, so the raw benchmark result can be
 * compared against the tier the engine actually ended up with.
 */
export async function probeDetectGpuRaw(): Promise<DetectGpuRawResult> {
    try {
        const result = await getGPUTier({
            mobileTiers: [0, 70, 90, 300],
            desktopTiers: [0, 50, 75, 300],
        });

        return {
            status: "ok",
            tier: result.tier,
            fps: result.fps,
            gpu: result.gpu,
            isMobile: result.isMobile ?? false,
        };
    } catch {
        return { status: "failed" };
    }
}

export function probeWebGl2(): WebGlProbeResult {
    const canvas = document.createElement("canvas");
    canvas.width = 1;
    canvas.height = 1;
    let gl: WebGL2RenderingContext | null = null;

    try {
        gl = canvas.getContext("webgl2", { failIfMajorPerformanceCaveat: true });
        if (!gl) {
            return { status: "unavailable" };
        }

        const debugInfo = gl.getExtension("WEBGL_debug_renderer_info");
        const renderer = debugInfo
            ? String(gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL))
            : String(gl.getParameter(gl.VERSION) ?? "unknown");

        if (isSoftwareRenderer(renderer)) {
            return { status: "software", renderer };
        }

        return { status: "hardware", renderer };
    } catch {
        return { status: "unavailable" };
    } finally {
        gl?.getExtension("WEBGL_lose_context")?.loseContext();
        canvas.remove();
    }
}

export async function probeWebGpu(): Promise<WebGpuProbeResult> {
    const navigatorLike = navigator as Navigator & { gpu?: NavigatorGpuLike };
    const gpu = navigatorLike.gpu;

    if (!gpu?.requestAdapter) {
        return { status: "unsupported" };
    }

    try {
        const adapter = await gpu.requestAdapter();
        if (!adapter) {
            return { status: "unavailable" };
        }

        let adapterInfo = adapter.info;
        if (!adapterInfo && adapter.requestAdapterInfo) {
            adapterInfo = await adapter.requestAdapterInfo().catch(() => undefined);
        }

        return {
            status: "available",
            // GPUAdapterInfo keeps its fields on the prototype, so a raw reference
            // serializes to {} in JSON exports — copy into a plain object.
            adapterInfo: adapterInfo
                ? {
                      vendor: adapterInfo.vendor,
                      architecture: adapterInfo.architecture,
                      device: adapterInfo.device,
                      description: adapterInfo.description,
                  }
                : undefined,
            isFallbackAdapter: adapter.isFallbackAdapter,
        };
    } catch {
        return { status: "unavailable" };
    }
}

export function classifyLayer1(webgl: WebGlProbeResult, webgpu: WebGpuProbeResult): Layer1Classification {
    if (webgl.status === "unavailable") {
        return {
            status: "acceleration-off",
            evidence: ["WebGL2 context unavailable with failIfMajorPerformanceCaveat=true"],
            hardwareAccelerated: false,
            corroboratedByWebGpu: webgpu.status === "unavailable",
        };
    }

    if (webgl.status === "software") {
        return {
            status: "acceleration-off",
            evidence: [`Software renderer detected (${webgl.renderer ?? "unknown"})`],
            hardwareAccelerated: false,
            corroboratedByWebGpu: webgpu.status === "unavailable",
        };
    }

    if (webgpu.status === "available") {
        return {
            status: "ok",
            evidence: ["WebGL2 hardware context available", "WebGPU adapter available"],
            hardwareAccelerated: true,
            corroboratedByWebGpu: true,
        };
    }

    return {
        status: "degraded",
        evidence: [
            "WebGL2 hardware context available",
            webgpu.status === "unsupported" ? "WebGPU unsupported/disabled" : "WebGPU adapter unavailable",
        ],
        hardwareAccelerated: true,
        corroboratedByWebGpu: false,
    };
}

export type Layer2Input = {
    /** Raw UNMASKED_RENDERER_WEBGL / WebGPU adapter string. */
    renderer?: string;
    /** Tier the engine is running with (Redux device profile). */
    tier?: number;
    /** Fresh detect-gpu run; its parsed `gpu` name is preferred over the raw renderer string. */
    detectGpu?: DetectGpuRawResult;
    /** WebGPU adapter info, used as a third identity source (vendor/architecture). */
    webGpuAdapterInfo?: GPUAdapterInfoLike;
};

export function classifyLayer2({ renderer, tier, detectGpu, webGpuAdapterInfo }: Layer2Input): Layer2Classification {
    // Identity evidence (name/vendor based) decides the status; performance evidence
    // (benchmark tier / engine tier) is a secondary signal that never flips identity.
    const integratedEvidence: string[] = [];
    const discreteEvidence: string[] = [];

    // detect-gpu's parsed model name (e.g. "intel iris xe graphics") is cleaner than the
    // raw ANGLE string, so match it first and fall back to the raw renderer string.
    const detectGpuName = detectGpu?.status === "ok" ? detectGpu.gpu : undefined;
    if (looksIntegrated(detectGpuName)) {
        integratedEvidence.push(`detect-gpu model name suggests integrated GPU (${detectGpuName})`);
    } else if (looksDiscrete(detectGpuName)) {
        discreteEvidence.push(`detect-gpu model name suggests discrete GPU (${detectGpuName})`);
    }

    if (looksIntegrated(renderer)) {
        integratedEvidence.push(`Renderer suggests integrated GPU (${renderer ?? "unknown"})`);
    } else if (looksDiscrete(renderer)) {
        discreteEvidence.push(`Renderer suggests discrete GPU (${renderer ?? "unknown"})`);
    }

    // Vendor "nvidia" / "apple" is unambiguous (no x86 iGPUs). Intel and AMD vendors
    // are inconclusive at vendor level (Arc is discrete, APUs are integrated), so they
    // only count when the full name matched above.
    const webGpuVendor = webGpuAdapterInfo?.vendor?.toLowerCase();
    if (webGpuVendor === "nvidia" || webGpuVendor === "apple") {
        discreteEvidence.push(
            `WebGPU adapter vendor is ${webGpuVendor} (${webGpuAdapterInfo?.architecture ?? "unknown architecture"})`,
        );
    }

    const weakGpuEvidence: string[] = [];
    if (detectGpu?.status === "ok" && typeof detectGpu.tier === "number" && detectGpu.tier <= 1) {
        weakGpuEvidence.push(`detect-gpu benchmark is low (tier=${detectGpu.tier}, fps=${detectGpu.fps ?? "n/a"})`);
    }
    if (typeof tier === "number" && tier >= 0 && tier <= 1) {
        weakGpuEvidence.push(`Engine device profile tier is low (tier=${tier})`);
    }
    const weakGpu = weakGpuEvidence.length > 0;

    // Integrated identity wins on conflict: the WebGL renderer string reflects the
    // adapter the engine actually renders on. (WebGPU can land on a different adapter —
    // an nvidia WebGPU adapter alongside an Intel WebGL renderer still means the engine
    // runs on the iGPU, and even confirms a discrete GPU is sitting idle.)
    if (integratedEvidence.length > 0) {
        return {
            status: "likely-igpu",
            evidence: integratedEvidence.concat(discreteEvidence),
            confidence: integratedEvidence.length >= 2 ? "high" : "medium",
            weakGpu,
            weakGpuEvidence,
        };
    }

    if (discreteEvidence.length > 0) {
        return {
            status: "likely-discrete",
            evidence: discreteEvidence,
            confidence: discreteEvidence.length >= 2 ? "high" : "medium",
            weakGpu,
            weakGpuEvidence,
        };
    }

    return {
        status: "unknown",
        evidence: ["No integrated- or discrete-GPU identity markers detected"],
        confidence: "low",
        weakGpu,
        weakGpuEvidence,
    };
}

type GpuIdentityHint = {
    renderer?: string;
    webGpuAdapterInfo?: GPUAdapterInfoLike;
};

/**
 * Chromium/Edge freeze third-party User-Agent Client Hints to Windows — and the
 * viewer runs in a cross-origin iframe inside Procore. Trust any Mac signal
 * (UA-CH, `navigator.platform`, UA string, or Apple/Metal GPU identity) over a
 * Windows UA-CH value so Edge on Mac does not get the Windows Get Started card.
 */
export function isWindows(gpuIdentity?: GpuIdentityHint): boolean {
    if (looksAppleGpu(gpuIdentity?.renderer, gpuIdentity?.webGpuAdapterInfo)) {
        return false;
    }

    const navigatorLike = navigator as Navigator & { userAgentData?: NavigatorUserAgentDataLike };
    const userAgentDataPlatform = navigatorLike.userAgentData?.platform;
    const navigatorPlatform = navigatorLike.platform;
    const userAgent = navigatorLike.userAgent;

    if (
        looksMac(userAgentDataPlatform) ||
        looksMac(navigatorPlatform) ||
        /macintosh|mac os x|iphone|ipad|ipod/i.test(userAgent)
    ) {
        return false;
    }

    if (typeof userAgentDataPlatform === "string" && userAgentDataPlatform.length > 0) {
        return userAgentDataPlatform.toLowerCase() === "windows";
    }

    return /win/i.test(navigatorPlatform) || /windows/i.test(userAgent);
}

function looksMac(value: string | undefined): boolean {
    return typeof value === "string" && /mac|iphone|ipad|ipod/i.test(value);
}

function looksAppleGpu(renderer: string | undefined, adapterInfo: GPUAdapterInfoLike | undefined): boolean {
    const identity = [
        renderer,
        adapterInfo?.vendor,
        adapterInfo?.architecture,
        adapterInfo?.device,
        adapterInfo?.description,
    ]
        .filter(Boolean)
        .join(" ");
    return /\bapple\b|\bmetal\b/i.test(identity);
}

function looksIntegrated(gpuName: string | undefined): boolean {
    if (!gpuName) {
        return false;
    }

    const normalized = gpuName.toLowerCase();
    if (!INTEGRATED_RENDERER_MARKERS.some((pattern) => pattern.test(normalized))) {
        return false;
    }

    // Discrete Arc A/B-series cards are not iGPUs. "Intel Arc Graphics" / 140V / 130T are.
    if (DISCRETE_INTEL_ARC_MARKER.test(normalized)) {
        return false;
    }

    // Vega APUs are integrated; dedicated RX Vega cards are not.
    return !/\brx\s+vega\b/i.test(normalized);
}

function looksDiscrete(gpuName: string | undefined): boolean {
    if (!gpuName) {
        return false;
    }

    const normalized = gpuName.toLowerCase();
    // Integrated markers take precedence: e.g. "AMD Radeon(TM) Graphics" is an APU even
    // though other Radeon names are discrete.
    if (looksIntegrated(normalized)) {
        return false;
    }

    return DISCRETE_RENDERER_MARKERS.some((pattern) => pattern.test(normalized));
}

function isSoftwareRenderer(renderer: string): boolean {
    const normalized = renderer.toLowerCase();
    return SOFTWARE_RENDERER_MARKERS.some((marker) => normalized.includes(marker));
}
